<template>
  <div class="d-inline-block">
    <button
      type="button"
      class="btn btn-sm btn-outline-secondary"
      :title="$t('notificationPrefs.title')"
      @click="open"
    >
      <i class="material-icons">notifications</i>
    </button>

    <div
      v-if="showModal"
      class="modal d-block"
      tabindex="-1"
      style="background-color: rgba(0, 0, 0, 0.5)"
      @click.self="close"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title d-flex align-items-center gap-2 mb-0">
              <i class="material-icons">notifications</i>
              {{ $t("notificationPrefs.title") }}
            </h5>
            <button type="button" class="btn-close" @click="close"></button>
          </div>

          <div class="modal-body">
            <ContentSkeleton v-if="loading" variant="form" :rows="6" padded />
            <div v-else-if="error" class="alert alert-danger mb-0">{{ error }}</div>
            <div v-else>
              <p class="text-muted small mb-3">{{ $t("notificationPrefs.intro") }}</p>

              <!-- App móvil -->
              <section class="np-section">
                <h6 class="np-section__title">
                  <i class="material-icons">smartphone</i>
                  {{ $t("notificationPrefs.mobileApp") }}
                  <label class="np-all form-check form-switch m-0">
                    <span class="np-all__label">{{ $t("notificationPrefs.all") }}</span>
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :checked="all.push"
                      :disabled="saving['push:*']"
                      @change="toggleAll('push', $event.target.checked)"
                    />
                  </label>
                </h6>
                <div class="np-device small mb-2" :class="{ 'np-device--on': pushState === 'on' }">
                  <span>{{ deviceText }}</span>
                  <button
                    v-if="pushState === 'on' || pushState === 'off'"
                    type="button"
                    class="btn btn-sm"
                    :class="pushState === 'on' ? 'btn-outline-secondary' : 'btn-primary'"
                    :disabled="pushBusy"
                    @click="togglePush"
                  >
                    {{ pushState === "on" ? $t("notificationPrefs.deviceDisable") : $t("notificationPrefs.deviceEnable") }}
                  </button>
                </div>
                <p v-if="pushHint" class="small text-muted mb-2">{{ pushHint }}</p>

                <div v-for="group in pushGroups" :key="'push-' + group.name" class="np-group">
                  <div class="np-group__name">{{ groupLabel(group.name) }}</div>
                  <label v-for="item in group.items" :key="'push-' + item.key" class="np-row">
                    <span class="np-row__text">
                      <span class="np-row__name">{{ item.name }}</span>
                      <span v-if="item.description" class="np-row__desc">{{ item.description }}</span>
                    </span>
                    <span class="form-check form-switch m-0">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="all.push && item.push"
                        :disabled="!all.push || saving[`push:${item.key}`]"
                        @change="toggle(item, 'push', $event.target.checked)"
                      />
                    </span>
                  </label>
                </div>
              </section>

              <!-- Email -->
              <section class="np-section">
                <h6 class="np-section__title">
                  <i class="material-icons">mail</i>
                  {{ $t("notificationPrefs.email") }}
                  <label class="np-all form-check form-switch m-0">
                    <span class="np-all__label">{{ $t("notificationPrefs.all") }}</span>
                    <input
                      type="checkbox"
                      class="form-check-input"
                      :checked="all.email"
                      :disabled="saving['email:*']"
                      @change="toggleAll('email', $event.target.checked)"
                    />
                  </label>
                </h6>
                <p class="small text-muted mb-2">{{ $t("notificationPrefs.emailNote") }}</p>
                <div v-for="group in emailGroups" :key="'email-' + group.name" class="np-group">
                  <div class="np-group__name">{{ groupLabel(group.name) }}</div>
                  <label v-for="item in group.items" :key="'email-' + item.key" class="np-row">
                    <span class="np-row__text">
                      <span class="np-row__name">{{ item.name }}</span>
                      <span v-if="item.description" class="np-row__desc">{{ item.description }}</span>
                    </span>
                    <span class="form-check form-switch m-0">
                      <input
                        type="checkbox"
                        class="form-check-input"
                        :checked="all.email && item.email"
                        :disabled="!all.email || saving[`email:${item.key}`]"
                        @change="toggle(item, 'email', $event.target.checked)"
                      />
                    </span>
                  </label>
                </div>
                <p v-if="!emailGroups.length" class="small text-muted mb-0">
                  {{ $t("notificationPrefs.noEmails") }}
                </p>
              </section>
            </div>
          </div>

          <div class="modal-footer">
            <button type="button" class="btn btn-secondary btn-sm" @click="close">
              {{ $t("Close") }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { wencesApi } from "@/api/wencesApi";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";
import { getPushState, enablePush, disablePush, PUSH_STATE_EVENT } from "@/utils/pushNotifications";

/**
 * Campanita del dashboard: cada usuario prende o apaga, para sí mismo, los avisos que le llegan
 * a la app del teléfono y por email. Lo que la empresa apagó en /emails ni aparece.
 */
export default {
  name: "NotificationPreferencesButton",
  components: { ContentSkeleton },
  data() {
    return {
      showModal: false,
      loading: false,
      error: "",
      emails: [],
      pushOnly: [],
      /** El «Todos» de cada canal: apagado, no sale nada por ahí y los de a uno quedan quietos. */
      all: { email: true, push: true },
      saving: {},
      pushState: "unsupported",
      pushBusy: false,
      pushHint: "",
    };
  },
  computed: {
    emailGroups() {
      return this.groupItems(this.emails);
    },
    /** Los emails de código y, al final, lo que es solo del teléfono (Agente IA, otros emails). */
    pushGroups() {
      const extra = this.pushOnly.map((item) => ({
        ...item,
        group: "",
        pushOnly: true,
        name: this.$t(`notificationPrefs.pushOnly.${item.key}.name`),
        description: this.$t(`notificationPrefs.pushOnly.${item.key}.description`),
      }));
      return this.groupItems([...this.emails, ...extra]);
    },
    deviceText() {
      return this.$t(`notificationPrefs.device.${this.pushState}`);
    },
  },
  beforeDestroy() {
    window.removeEventListener(PUSH_STATE_EVENT, this.onPushStateChanged);
  },
  methods: {
    groupItems(items) {
      const groups = [];
      for (const item of items) {
        let group = groups.find((g) => g.name === item.group);
        if (!group) groups.push((group = { name: item.group, items: [] }));
        group.items.push(item);
      }
      return groups;
    },
    groupLabel(name) {
      if (!name) return this.$t("notificationPrefs.groups.other");
      const key = `notificationPrefs.groups.${name}`;
      return this.$te(key) ? this.$t(key) : name;
    },
    async open() {
      this.showModal = true;
      this.loading = true;
      this.error = "";
      this.pushHint = "";
      window.addEventListener(PUSH_STATE_EVENT, this.onPushStateChanged);
      try {
        const [{ data }, state] = await Promise.all([
          wencesApi.get("/api/notification-preferences"),
          getPushState().catch(() => "unsupported"),
        ]);
        this.emails = data.emails || [];
        this.pushOnly = data.pushOnly || [];
        this.all = { email: data.emailAll !== false, push: data.pushAll !== false };
        this.pushState = state;
      } catch (e) {
        this.error = (e.response && e.response.data && e.response.data.error) || e.message;
      } finally {
        this.loading = false;
      }
    },
    close() {
      this.showModal = false;
      window.removeEventListener(PUSH_STATE_EVENT, this.onPushStateChanged);
    },
    onPushStateChanged(e) {
      if (e.detail) this.pushState = e.detail.state;
    },
    async toggle(item, channel, enabled) {
      const id = `${channel}:${item.key}`;
      const list = item.pushOnly ? this.pushOnly : this.emails;
      const target = list.find((i) => i.key === item.key);
      this.$set(this.saving, id, true);
      if (target) target[channel] = enabled;
      try {
        await wencesApi.put("/api/notification-preferences", { channel, key: item.key, enabled });
      } catch (e) {
        if (target) target[channel] = !enabled;
        this.$notify({
          message: (e.response && e.response.data && e.response.data.error) || e.message,
          type: "danger",
        });
      } finally {
        this.$set(this.saving, id, false);
      }
    },
    async toggleAll(channel, enabled) {
      const id = `${channel}:*`;
      this.$set(this.saving, id, true);
      this.all[channel] = enabled;
      try {
        await wencesApi.put("/api/notification-preferences", { channel, key: "*", enabled });
      } catch (e) {
        this.all[channel] = !enabled;
        this.$notify({
          message: (e.response && e.response.data && e.response.data.error) || e.message,
          type: "danger",
        });
      } finally {
        this.$set(this.saving, id, false);
      }
    },
    async togglePush() {
      if (this.pushBusy) return;
      this.pushBusy = true;
      this.pushHint = "";
      try {
        if (this.pushState === "on") {
          await disablePush();
          this.pushState = "off";
          return;
        }
        const result = await enablePush();
        if (result === "denied") this.pushHint = this.$t("mobile.pushDenied");
        else if (result === "unavailable") this.pushHint = this.$t("ai.agentChatNotifyUnavailable");
        else this.pushState = "on";
      } catch (e) {
        this.pushHint = (e.response && e.response.data && e.response.data.error) || e.message;
      } finally {
        this.pushBusy = false;
      }
    },
  },
};
</script>

<style scoped>
.np-section + .np-section {
  margin-top: 1.5rem;
}
.np-section__title {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
}
.np-all {
  margin-left: auto !important;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 400;
  font-size: 0.85rem;
  cursor: pointer;
}
.np-all .form-check-input {
  float: none;
  margin: 0;
  cursor: pointer;
}
.np-section__title .material-icons {
  font-size: 20px;
}
.np-device {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  border-radius: 0.375rem;
  background: var(--bs-tertiary-bg, rgba(0, 0, 0, 0.04));
  flex-wrap: wrap;
}
/* En el teléfono el botón baja a su propio renglón en vez de aplastarse. */
.np-device > span {
  flex: 1 1 12rem;
}
.np-device .btn {
  white-space: nowrap;
}
.np-group {
  margin-bottom: 0.75rem;
}
.np-group__name {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--bs-secondary-color, #6c757d);
  margin: 0.5rem 0 0.25rem;
}
.np-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0.4rem 0;
  border-bottom: 1px solid var(--bs-border-color, #dee2e6);
  cursor: pointer;
  margin: 0;
}
.np-row__text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.np-row__name {
  font-weight: 500;
}
.np-row__desc {
  font-size: 0.8rem;
  color: var(--bs-secondary-color, #6c757d);
}
.np-row .form-check-input {
  cursor: pointer;
}
</style>
